'use client'

import { Box } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'
import { type ComponentProps, useEffect, useRef, useState } from 'react'
import Markdown, { type Components, type ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'

type AssistantMarkdownProps = {
  children: string
}

/**
 * Renders one assistant reply in the chat bar, a shared conversation, or the billing composer.
 *
 * Pipe tables need remark-gfm; without it they stay proportional-font text and the
 * columns never line up. The hast converter also inserts newline text nodes between
 * blocks and list items. The chat bubble's `white-space: pre-wrap` prints those as
 * blank lines, so lists and paragraphs look double-spaced. `normal` collapses that
 * structural whitespace; `remarkSoftBreaks` keeps a single newline inside a paragraph.
 */
export default function AssistantMarkdown({ children }: AssistantMarkdownProps) {
  return (
    <MarkdownBody data-cy="assistant-markdown">
      <Markdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
        {children}
      </Markdown>
    </MarkdownBody>
  )
}

function MarkdownLink({ node, children, ...props }: ComponentProps<'a'> & ExtraProps) {
  void node
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

function MarkdownTable({ node, children, ...props }: ComponentProps<'table'> & ExtraProps) {
  void node
  const { ref, edges } = useOverflowEdges()
  return (
    <TableScroll ref={ref} data-overflow-start={edges.start || undefined} data-overflow-end={edges.end || undefined}>
      <table {...props}>{children}</table>
    </TableScroll>
  )
}

/** Which horizontal edges of a scroll container have content clipped behind them, kept current on scroll and resize. */
function useOverflowEdges() {
  const ref = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: false, end: false })

  useEffect(() => {
    const container = ref.current
    if (!container) return undefined
    const measure = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setEdges({ start: scrollLeft > 0, end: scrollLeft + clientWidth < scrollWidth - 1 })
    }
    measure()
    container.addEventListener('scroll', measure, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure)
    observer?.observe(container)
    return () => {
      container.removeEventListener('scroll', measure)
      observer?.disconnect()
    }
  }, [])

  return { ref, edges }
}

const REMARK_PLUGINS = [remarkGfm, remarkSoftBreaks]

const COMPONENTS: Components = {
  a: MarkdownLink,
  table: MarkdownTable
}

const MarkdownBody = styled(Box)(({ theme }) => ({
  minWidth: 0,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.6,
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'break-word',
  '& p, & h1, & h2, & h3, & h4, & h5, & h6, & ul, & ol, & pre, & blockquote, & hr': {
    margin: 0
  },
  // Doubled class so this beats the element margin reset above.
  '&& > * + *': {
    marginTop: theme.spacing(2)
  },
  '& h1': {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: 700,
    lineHeight: 1.3
  },
  '& h2': {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 700,
    lineHeight: 1.35
  },
  '& h3, & h4, & h5, & h6': {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 650,
    lineHeight: 1.4
  },
  '& strong': { fontWeight: 650 },
  '& ul, & ol': {
    padding: 0,
    paddingInlineStart: theme.spacing(3),
    listStylePosition: 'outside'
  },
  '& li + li': { marginTop: theme.spacing(1) },
  '& li > ul, & li > ol': { marginTop: theme.spacing(0.75) },
  '& li > p + p': { marginTop: theme.spacing(1) },
  '& li::marker': { color: theme.palette.text.primary },
  '& li > input[type="checkbox"]': {
    marginRight: theme.spacing(0.75),
    accentColor: theme.palette.primary.main,
    verticalAlign: 'middle'
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: '2px'
  },
  '& blockquote': {
    padding: theme.spacing(0.25, 0, 0.25, 1.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary
  },
  '& blockquote > * + *': { marginTop: theme.spacing(1) },
  '& hr': {
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '0.85em',
    whiteSpace: 'pre-wrap',
    backgroundColor: alpha(theme.palette.text.primary, 0.08),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '1px 5px'
  },
  '& pre': {
    whiteSpace: 'pre-wrap',
    padding: theme.spacing(1.25),
    borderRadius: theme.spacing(1),
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    border: `1px solid ${theme.palette.divider}`,
    overflowX: 'auto',
    '& code': {
      whiteSpace: 'pre-wrap',
      backgroundColor: 'transparent',
      border: 0,
      padding: 0
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius
  },
  '& table': {
    borderCollapse: 'collapse',
    width: 'max-content',
    minWidth: '100%',
    fontVariantNumeric: 'tabular-nums'
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.75, 1),
    verticalAlign: 'top',
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'break-word'
  },
  '& th': {
    fontWeight: 650,
    backgroundColor: theme.palette.background.paper
  }
}))

/** Width of the fade on an edge with more table behind it. */
const EDGE_FADE = '24px'

// A clipped edge fades out so the table reads as scrollable; the mask leaves an unclipped edge untouched.
const TableScroll = styled(Box)({
  maxWidth: '100%',
  overflowX: 'auto',
  '&[data-overflow-end]': {
    maskImage: `linear-gradient(to right, black calc(100% - ${EDGE_FADE}), transparent)`
  },
  '&[data-overflow-start]': {
    maskImage: `linear-gradient(to left, black calc(100% - ${EDGE_FADE}), transparent)`
  },
  '&[data-overflow-start][data-overflow-end]': {
    maskImage: `linear-gradient(to right, transparent, black ${EDGE_FADE}, black calc(100% - ${EDGE_FADE}), transparent)`
  }
})

type MdastNode = {
  type: string
  value?: string
  children?: MdastNode[]
}

/** Turn a single newline inside a text node into a hard break. Blank lines are already separate blocks. */
function remarkSoftBreaks() {
  return (tree: MdastNode) => {
    convertSoftBreaks(tree)
  }
}

function convertSoftBreaks(node: MdastNode): void {
  if (!node.children) return
  const next: MdastNode[] = []
  for (const child of node.children) {
    if (child.type === 'text' && child.value?.includes('\n')) {
      const parts = child.value.split('\n')
      parts.forEach((part, index) => {
        if (index > 0) next.push({ type: 'break' })
        if (part.length > 0) next.push({ type: 'text', value: part })
      })
      continue
    }
    convertSoftBreaks(child)
    next.push(child)
  }
  node.children = next
}
